import { Injectable, inject } from '@angular/core';
import { EMPTY, Observable, Subject, catchError, defer, filter, shareReplay, startWith, switchMap } from 'rxjs';
import { WeatherApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/weather/weather-api';
import { LocationListResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/location-list.response';
import { ClimateType } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/climate-type.enum';
import { AddressApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/address/address-api';
import { AddressResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/address.response';
import { LocalFrostDatesResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/local-frost-dates.response';
import { GardenStore } from '../@commons/@services/data/garden.store';
import { DataStore } from '../@commons/@services/data/data-store.service';
import { StorageService } from '../@commons/@services/front/storage.service';

/**
 * Zones et climat de chaque adresse du profil, chargés UNE fois par adresse.
 *
 * Chaque carte d'adresse lisait ses zones, et la feuille d'édition qu'elle
 * embarque (toujours montée, même fermée) les relisait aussitôt ; chaque carte
 * relisait aussi le climat. Avec plusieurs adresses, la rafale dépassait la
 * limite du back (10 requêtes/s) et les 429 vidaient les cartes.
 *
 * Les zones viennent du `GardenStore`, partagées avec Mon potager ; une
 * écriture les invalide d'elle-même. Après une modification, `refresh` les
 * relit et les pousse à tous les lecteurs de l'adresse.
 *
 * La fiche d'une adresse (« Mon potager » du profil) y lit aussi l'adresse
 * complète et ses dates de gel, par le `DataStore` : une adresse modifiée
 * invalide ces lectures, et `refresh` les relit.
 */
@Injectable()
export class AddressLocationsService {
  // Les classes du SDK lisent leur configuration dans un singleton : on les
  // instancie directement, comme PremiumService.
  private readonly weatherApi = new WeatherApi();
  private readonly addressApi = new AddressApi();
  private readonly gardenStore = inject(GardenStore);
  private readonly dataStore = inject(DataStore);
  private readonly storage = inject(StorageService);

  private readonly refreshes = new Subject<string>();
  private readonly locationStreams = new Map<string, Observable<LocationListResponse[]>>();
  private readonly climateStreams = new Map<string, Observable<ClimateType | null>>();
  private readonly addressStreams = new Map<string, Observable<AddressResponse>>();
  private readonly frostStreams = new Map<string, Observable<LocalFrostDatesResponse>>();

  /**
   * Zones de l'adresse, partagées entre tous les lecteurs.
   *
   * Un échec n'émet rien (la carte n'affiche pas de compte) plutôt qu'une
   * liste vide qui ferait croire à une adresse sans zone.
   */
  locations$(addressUid: string): Observable<LocationListResponse[]> {
    return this.shared(this.locationStreams, addressUid, () => this.gardenStore.zones(addressUid));
  }

  /**
   * Climat de CETTE adresse (et non de l'adresse enregistrée comme principale).
   * `null` : le serveur a répondu sans climat. Un échec n'émet rien : on ne
   * prétend pas « aucun climat » sans le savoir.
   */
  climate$(addressUid: string): Observable<ClimateType | null> {
    return this.shared(this.climateStreams, addressUid, () =>
      this.weatherApi.getClimate(addressUid).then((res) => (res?.climate as ClimateType) ?? null),
    );
  }

  /**
   * L'adresse complète (rue, coordonnées) : la liste du profil n'en donne que
   * la commune. Sans coordonnées, l'adresse n'est pas encore située.
   */
  address$(addressUid: string): Observable<AddressResponse> {
    return this.shared(this.addressStreams, addressUid, () =>
      this.dataStore.get(`user/address:${addressUid}`, () =>
        this.addressApi.find(addressUid, this.storage.getCurrentLanguage()),
      ),
    );
  }

  /** Dates de gel de CETTE adresse (et non de l'adresse enregistrée comme principale). */
  frostDates$(addressUid: string): Observable<LocalFrostDatesResponse> {
    return this.shared(this.frostStreams, addressUid, () =>
      this.dataStore.get(`weather/frost:${addressUid}`, () => this.weatherApi.getFrostDates(addressUid)),
    );
  }

  /** L'adresse dont l'app montre la météo, les alertes et Mon potager. */
  isAppAddress(addressUid: string): boolean {
    return !!addressUid && this.storage.getItem('address-uid') === addressUid;
  }

  /** Relit zones, climat, adresse et dates de gel après une création ou une modification. */
  refresh(addressUid: string): void {
    this.refreshes.next(addressUid);
  }

  /**
   * Un flux par adresse, partagé par tous ses lecteurs et relu par `refresh`.
   * Un échec n'émet rien : on ne remplace pas une donnée inconnue par du vide.
   */
  private shared<T>(streams: Map<string, Observable<T>>, addressUid: string, read: () => Promise<T>): Observable<T> {
    let stream = streams.get(addressUid);
    if (!stream) {
      stream = this.refreshes.pipe(
        filter((uid) => uid === addressUid),
        startWith(addressUid),
        switchMap(() => defer(read).pipe(catchError(() => EMPTY))),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
      streams.set(addressUid, stream);
    }
    return stream;
  }
}
